import { useEffect, useState } from 'react';
import { Outlet, NavLink, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { SkipToContent } from '@/components/SkipToContent';
import { SignOutButton } from '@/components/SignOutButton';
import { LanguageDropdown } from '@/components/LanguageDropdown';
import { APP } from '@/config/app';
import { useAuth } from '@/contexts/AuthContext';
import { formatGstDateTime } from '@/lib/format';
import { privateMeta } from '@/lib/seo/site';
import { useSeoHead } from '@/lib/seo/useSeoHead';

const TOPBAR_CLOCK_REFRESH_MS = 60_000;

const NAV_ITEMS = [
  { to: '/admin', key: 'kpi', end: true },
  { to: '/admin/deals', key: 'deals' },
  { to: '/admin/submissions', key: 'submissions' },
  { to: '/admin/requests', key: 'requests' },
  { to: '/admin/users', key: 'users' },
  { to: '/admin/emails', key: 'emails' },
  { to: '/admin/cms', key: 'cms' },
  { to: '/admin/advisors', key: 'advisors' },
  { to: '/admin/settings', key: 'settings' },
] as const;

export function AdminLayout() {
  const { t } = useTranslation();
  useSeoHead(privateMeta('Back office'));
  const auth = useAuth();
  const location = useLocation();
  const adminEmail = auth.status === 'authenticated' ? auth.user.email : '';
  const adminRole = auth.status === 'authenticated' ? auth.profile?.role : undefined;
  const roleBadge =
    adminRole === 'super_admin'
      ? t('admin.topbar.roleSuperAdmin')
      : adminRole === 'admin'
        ? t('admin.topbar.roleAdmin')
        : null;
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(() => formatGstDateTime());

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  // Tick the topbar clock once a minute. Output format only shows minute
  // granularity, so a tighter interval is wasted CPU.
  useEffect(() => {
    const id = window.setInterval(() => {
      setCurrentTime(formatGstDateTime());
    }, TOPBAR_CLOCK_REFRESH_MS);
    return () => window.clearInterval(id);
  }, []);

  return (
    <div
      className={`layout layout--admin admin-shell${mobileNavOpen ? ' is-mobile-nav-open' : ''}`}
    >
      <SkipToContent />
      <aside className="admin-sidebar" aria-label={t('admin.nav.label')}>
        <Link to="/admin" className="admin-sidebar__brand" aria-label={APP.brand}>
          <img src="/logo.svg" alt="" aria-hidden="true" className="brand__logo" />
        </Link>
        <nav className="admin-sidebar__nav">
          {NAV_ITEMS.map((item) => (
            <NavLink key={item.to} to={item.to} end={'end' in item ? item.end : false}>
              {t(`admin.nav.${item.key}`)}
            </NavLink>
          ))}
        </nav>
        <div className="admin-sidebar__footer">
          <span className="admin-sidebar__user">{adminEmail}</span>
          <Link to="/" className="admin-sidebar__view-site">
            {t('admin.nav.viewSite')}
          </Link>
          <SignOutButton variant="admin" />
        </div>
      </aside>
      <button
        type="button"
        className="admin-sidebar-backdrop"
        aria-label={t('admin.nav.closeAria', 'Close navigation')}
        onClick={() => setMobileNavOpen(false)}
      />
      <div className="admin-main-wrap">
        <header className="admin-topbar" role="banner">
          <button
            type="button"
            className="admin-topbar__hamburger"
            aria-label={t('admin.nav.openAria', 'Open navigation')}
            aria-expanded={mobileNavOpen}
            onClick={() => setMobileNavOpen((v) => !v)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M3 6h18M3 12h18M3 18h18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
          <span className="admin-topbar__title">
            {t('admin.topbar.title')}
            {roleBadge ? <span className="admin-topbar__role-badge">{roleBadge}</span> : null}
          </span>
          <div className="admin-topbar__actions">
            <LanguageDropdown />
            <span className="admin-topbar__time">{currentTime}</span>
          </div>
        </header>
        <main id="main-content" className="admin-main" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
